// Formula evaluator for the booking "Calc" price sheet.
//
// A sheet is a sparse map keyed by cell coordinate ("A1".."D20"):
//   { "A1": { raw: "10" }, "B1": { raw: "=A1*2" }, ... }
//
// Each cell's `raw` is the exact string the user typed. `computeSheet` returns
// a plain map of coordinate -> computed value (number | string | "#ERROR"),
// omitting empty cells. Supported formulas: + - * /, parentheses, cell
// references, and SUM(range | args...). Everything is evaluated client-side;
// the backend only stores raw + value.

export const CELL_ERROR = "#ERROR";

const NUMBER_LITERAL_RE = /^[+-]?(\d+\.?\d*|\.\d+)$/;
const CELL_REF_RE = /^[A-Za-z]+\d+$/;

class FormulaError extends Error {}

export function classifyCell(raw) {
  if (raw == null) return "empty";
  const trimmed = String(raw).trim();
  if (trimmed === "") return "empty";
  if (trimmed.startsWith("=")) return "formula";
  if (NUMBER_LITERAL_RE.test(trimmed)) return "number";
  return "text";
}

// Reject Infinity / NaN (e.g. divide by zero) and strip binary-float noise.
// toPrecision(15) removes the noise (0.1+0.2 -> 0.3) without the overflow that
// `n * 1e10` causes for large values — VND amounts routinely exceed the safe
// range where that multiply loses integer precision.
function cleanNumber(n) {
  if (!Number.isFinite(n)) throw new FormulaError();
  return Number(n.toPrecision(15));
}

export function computeSheet(cells) {
  // Uppercase-keyed raw lookup so references resolve case-insensitively.
  const rawByRef = {};
  for (const key of Object.keys(cells)) {
    const entry = cells[key];
    rawByRef[key.toUpperCase()] =
      entry && entry.raw != null ? String(entry.raw) : "";
  }

  const memo = {}; // ref -> value (number | string | CELL_ERROR | undefined for empty)
  const state = {}; // ref -> "visiting" | "done"

  function evalRef(ref) {
    if (state[ref] === "done") return memo[ref];
    if (state[ref] === "visiting") throw new FormulaError(); // circular

    const kind = classifyCell(rawByRef[ref]);
    if (kind === "empty") {
      state[ref] = "done";
      memo[ref] = undefined;
      return undefined;
    }

    state[ref] = "visiting";
    let value;
    try {
      const trimmed = rawByRef[ref].trim();
      if (kind === "number") value = cleanNumber(Number(trimmed));
      else if (kind === "text") value = trimmed;
      else value = evalFormula(trimmed.slice(1));
    } catch (e) {
      value = CELL_ERROR;
    }
    state[ref] = "done";
    memo[ref] = value;
    return value;
  }

  // Numeric value of a bare reference used inside an arithmetic expression.
  function refAsNumber(ref) {
    const v = evalRef(ref);
    if (v === undefined) return 0; // empty cell behaves as 0
    if (v === CELL_ERROR) throw new FormulaError();
    if (typeof v === "number") return v;
    throw new FormulaError(); // text in arithmetic
  }

  // Contribution of one cell to a SUM: numbers add, empty/text are ignored,
  // errors propagate.
  function refInSum(ref) {
    const v = evalRef(ref);
    if (v === CELL_ERROR) throw new FormulaError();
    return typeof v === "number" ? v : 0;
  }

  function evalFormula(src) {
    const tokens = tokenize(src);
    let pos = 0;

    const peek = (offset = 0) => tokens[pos + offset];
    const next = () => tokens[pos++];
    const expect = (type) => {
      const t = tokens[pos];
      if (!t || t.type !== type) throw new FormulaError();
      return tokens[pos++];
    };

    function parseExpr() {
      let value = parseTerm();
      while (peek() && (peek().type === "+" || peek().type === "-")) {
        const op = next().type;
        const rhs = parseTerm();
        value = op === "+" ? value + rhs : value - rhs;
      }
      return value;
    }

    function parseTerm() {
      let value = parseFactor();
      while (peek() && (peek().type === "*" || peek().type === "/")) {
        const op = next().type;
        const rhs = parseFactor();
        if (op === "*") value = value * rhs;
        else {
          if (rhs === 0) throw new FormulaError();
          value = value / rhs;
        }
      }
      return value;
    }

    function parseFactor() {
      const t = peek();
      if (!t) throw new FormulaError();
      if (t.type === "+") {
        next();
        return parseFactor();
      }
      if (t.type === "-") {
        next();
        return -parseFactor();
      }
      if (t.type === "num") {
        next();
        return t.value;
      }
      if (t.type === "(") {
        next();
        const value = parseExpr();
        expect(")");
        return value;
      }
      if (t.type === "ident") {
        return parseIdent();
      }
      throw new FormulaError();
    }

    function parseIdent() {
      const ident = next().value;
      if (peek() && peek().type === "(") {
        if (ident.toUpperCase() !== "SUM") throw new FormulaError();
        return parseSum();
      }
      if (!CELL_REF_RE.test(ident)) throw new FormulaError();
      return refAsNumber(ident.toUpperCase());
    }

    function parseSum() {
      expect("(");
      let total = parseSumArg();
      while (peek() && peek().type === ",") {
        next();
        total += parseSumArg();
      }
      expect(")");
      return total;
    }

    function parseSumArg() {
      const t = peek();
      // Range (A1:B2) or a single cell reference get SUM semantics
      // (text/empty ignored). Anything else is a plain sub-expression.
      if (t && t.type === "ident" && CELL_REF_RE.test(t.value)) {
        const nextTok = peek(1);
        if (nextTok && nextTok.type === ":") {
          const from = next().value;
          next(); // consume ":"
          const to = expect("ident").value;
          return sumRange(from.toUpperCase(), to.toUpperCase());
        }
        if (nextTok && (nextTok.type === "," || nextTok.type === ")")) {
          next();
          return refInSum(t.value.toUpperCase());
        }
      }
      return parseExpr();
    }

    const result = parseExpr();
    if (pos !== tokens.length) throw new FormulaError(); // trailing tokens
    return cleanNumber(result);
  }

  function sumRange(fromRef, toRef) {
    const from = splitRef(fromRef);
    const to = splitRef(toRef);
    if (!from || !to) throw new FormulaError();
    const colLo = Math.min(from.col, to.col);
    const colHi = Math.max(from.col, to.col);
    const rowLo = Math.min(from.row, to.row);
    const rowHi = Math.max(from.row, to.row);

    let total = 0;
    for (let col = colLo; col <= colHi; col++) {
      for (let row = rowLo; row <= rowHi; row++) {
        total += refInSum(colToLetters(col) + row);
      }
    }
    return total;
  }

  const result = {};
  for (const key of Object.keys(cells)) {
    const value = evalRef(key.toUpperCase());
    if (value !== undefined) result[key] = value;
  }
  return result;
}

function tokenize(src) {
  const tokens = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i];
    if (c === " " || c === "\t") {
      i++;
      continue;
    }
    if ("+-*/(),:".includes(c)) {
      tokens.push({ type: c });
      i++;
      continue;
    }
    if (/[0-9.]/.test(c)) {
      let j = i;
      while (j < src.length && /[0-9.]/.test(src[j])) j++;
      const text = src.slice(i, j);
      if (!NUMBER_LITERAL_RE.test(text)) throw new FormulaError();
      tokens.push({ type: "num", value: Number(text) });
      i = j;
      continue;
    }
    if (/[A-Za-z]/.test(c)) {
      let j = i;
      while (j < src.length && /[A-Za-z0-9]/.test(src[j])) j++;
      tokens.push({ type: "ident", value: src.slice(i, j) });
      i = j;
      continue;
    }
    throw new FormulaError();
  }
  return tokens;
}

// Display formatting for a computed cell value. Numbers get Vietnamese-style
// grouping ("." for thousands, "," for the decimal part): 1000000 -> "1.000.000",
// 1234.5 -> "1.234,5". Text and errors pass through unchanged.
export function formatValue(value) {
  if (typeof value !== "number") return String(value);
  const digits = String(Math.abs(value));
  // Exponential notation (|value| >= 1e21) can't be grouped; show it raw.
  if (digits.includes("e")) return String(value);
  const sign = value < 0 ? "-" : "";
  const [intPart, fracPart] = digits.split(".");
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return sign + grouped + (fracPart != null ? "," + fracPart : "");
}

// "A1" -> { col: 1, row: 1 }; "AA10" -> { col: 27, row: 10 }
function splitRef(ref) {
  const m = /^([A-Z]+)(\d+)$/.exec(ref);
  if (!m) return null;
  let col = 0;
  for (const ch of m[1]) col = col * 26 + (ch.charCodeAt(0) - 64);
  return { col, row: Number(m[2]) };
}

function colToLetters(col) {
  let letters = "";
  let n = col;
  while (n > 0) {
    const rem = (n - 1) % 26;
    letters = String.fromCharCode(65 + rem) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters;
}
